<script setup lang="ts">
import VTooltip from '~/components/ui/VTooltip.vue'

import { useSegmentBucketList } from '~/composables/useSegmentBucketList'

import { useUserFavoritesStore } from '~/stores/userFavorites'

import type { Influencer, StatsV3Influencer } from '~/types/influencer'
import type { OriginPageName } from '~/types/Segment/SegmentEventLibrary/SegmentEventLibraryBucketList'

type Props = {
  campaignId?: number
  influencer?: Influencer | StatsV3Influencer | undefined
  hideTooltipArrow?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  campaignId: undefined,
  influencer: undefined,
  hideTooltipArrow: true,
})

const route = useRoute()
const { trackListSaveInitiated } = useSegmentBucketList()

const {
  INF_ID_IS_IN_BUCKET,
  SET_INSPECTED_INFLUENCERS,
  SET_ORIGIN_PAGE,
  SET_CAMPAIGN_ID,
} = useUserFavoritesStore()

const isInBucket = computed(() => {
  return props.influencer ? INF_ID_IS_IN_BUCKET(props.influencer.id) : false
})

const originPage = computed<OriginPageName>(() => {
  const { getOriginPage } = useCuratorAddGetOriginPage(route)
  return getOriginPage() || 'curator-card'
})

const bookmarkedClass = computed(() => {
  let bookmarkClassList = ''

  bookmarkClassList = isInBucket.value
    ? 'fas tw-bg-gradient-135 tw-bg-grooverGradient'
    : 'far hover:tw-bg-gradient-135 hover:tw-bg-grooverGradient'

  bookmarkClassList +=
    originPage.value === 'curator-profile'
      ? ' tw-text-white md:tw-text-gray-500 hover:tw-text-transparent'
      : ' tw-text-gray-500 hover:tw-text-transparent'

  return bookmarkClassList
})

function openBucketModal() {
  if (props.influencer) {
    SET_INSPECTED_INFLUENCERS([props.influencer])
    SET_ORIGIN_PAGE(originPage.value)

    if (props.campaignId) SET_CAMPAIGN_ID(props.campaignId)

    trackListSaveInitiated([props.influencer])
  }
}
</script>

<template>
  <VTooltip
    position="below"
    padding-class="tw-px-3 tw-py-2"
    :hide-tooltip-arrow="hideTooltipArrow"
    data-test-id="vBookmark"
  >
    <button
      type="button"
      data-test-id="vBookmarkIcon"
      @click.stop="openBucketModal"
    >
      <i
        :class="`fa-bookmark tw-cursor-pointer tw-rounded-xs tw-bg-clip-text tw-text-base ${bookmarkedClass}`"
      />
      <span class="tw-sr-only">{{
        isInBucket
          ? $t('agency.favorites.savedToBucket')
          : $t('agency.favorites.saveToBucket')
      }}</span>
    </button>
    <template #tooltipText>
      {{
        isInBucket
          ? $t('agency.favorites.savedToBucket')
          : $t('agency.favorites.saveToBucket')
      }}
    </template>
  </VTooltip>
</template>
